import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
    getAwardedCampaignData,
    getAwardedCampaignSummaryData,
    getPromotionsErrorData,
} from '../../../../redux/selectors/backoffice/propmotions-selectors';
import {
    clearCampaignData,
    getAwardedCampaign,
} from '../../../../redux/actions/backoffice/promotions-actions';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs, Button, Tooltip } from 'antd';
import { currencyUtils } from 'utils';
import PromotionalTotalComponent from './PromotionalTotalComponent';
import dayjs from 'dayjs';
import { ArrowLeftOutlined } from '@ant-design/icons';
import CampaignHeaderButton from './CampaignHeaderButton';
import PlayersTable from './Tabs/PlayersTable';
import GamesTable from './Tabs/GamesTable';
import {
    canManagePromotionCampaign,
    canViewThirdPartyCampaignFeatures,
    getCampaignOwnerDisplayName,
} from '../../promotionUtils';

const AwardedCampaignControl = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const campaignData = useSelector(getAwardedCampaignData);
    const campaignSummary = useSelector(getAwardedCampaignSummaryData);
    const error = useSelector(getPromotionsErrorData);
    const userActiveCurrency = currencyUtils.getActiveCurrencyData();
    const canViewThirdPartyFeatures = canViewThirdPartyCampaignFeatures();

    const [currency, setCurrency] = useState<'user' | 'campaign'>(
        userActiveCurrency.billable ? 'user' : 'campaign',
    );
    const [currencyCode, setCurrencyCode] = useState(
        currency === 'user' ? userActiveCurrency.code : '',
    );

    const isUserCanManageCampaigns = canManagePromotionCampaign(campaignData);

    const requestCampaign = useCallback(() => {
        dispatch(
            getAwardedCampaign({
                campaignId: params.campaignId,
                currency: currency,
            }),
        );
    }, [dispatch, params.campaignId, currency]);

    useEffect(() => {
        requestCampaign();
    }, [requestCampaign]);

    useEffect(() => {
        return () => {
            dispatch(clearCampaignData());
            setCurrency('user');
            setCurrencyCode(userActiveCurrency.code);
        };
    }, [dispatch, userActiveCurrency.code]);

    const handleUpdateCurrency = (value: 'user' | 'campaign') => {
        setCurrency(value);
        setCurrencyCode(
            value === 'user' ? userActiveCurrency.code : campaignData?.currencyCode || '',
        );
    };

    const handleEditClick = () => {
        navigate(`/casino/awarded-promotion/${params.campaignId}/edit`);
    };

    return (
        <div className="min-h-0 h-[calc(100vh-64px)] overflow-hidden bg-[#fafbfc] flex flex-col pb-4">
            {/* Header: Breadcrumbs, Title, Edit Button */}
            <div className="flex justify-between items-center px-6 pt-4 pb-4">
                <div className="flex items-center gap-2">
                    <Button
                        icon={<ArrowLeftOutlined />}
                        type="text"
                        onClick={() => navigate(-1)}
                        className="p-6 font-medium"
                    />
                    <div className="">
                        <div className="text-xs text-gray-400 mb-1">
                            <Link
                                to="/casino/awarded-promotions"
                                className="hover:underline text-gray-400"
                            >
                                Promotions
                            </Link>
                        </div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            {campaignData?.name || ''}
                        </h1>
                    </div>
                </div>
                <div className="">
                    {campaignData?.status !== 'ENDED' && campaignData?.status !== 'DISABLED' && isUserCanManageCampaigns && (
                        <Tooltip
                            title={
                                campaignData?.status === 'ONGOING'
                                    ? 'Changes cannot be made once the campaign has started'
                                    : ''
                            }
                            placement="bottom"
                        >
                            <Button
                                shape="round"
                                onClick={handleEditClick}
                                className="px-6 h-10 text-base font-medium mr-3"
                                disabled={campaignData?.status === 'ONGOING'}
                            >
                                Edit
                            </Button>
                        </Tooltip>
                    )}
                    {campaignData?.status === 'ONGOING' && isUserCanManageCampaigns && (
                        <>
                            <Tooltip
                                title={
                                    campaignData?.strategy !== 'PLAYER_LIST'
                                        ? 'Manage Players is only available for PLAYER_LIST strategy.'
                                        : ''
                                }
                                placement="bottom"
                            >
                                <Button
                                    shape="round"
                                    onClick={() =>
                                        navigate(
                                            `/casino/awarded-promotion/${params.campaignId}/manage-players`,
                                        )
                                    }
                                    className="px-6 h-10 text-base font-medium mr-3"
                                    disabled={campaignData?.strategy !== 'PLAYER_LIST'}
                                >
                                    Manage Players
                                </Button>
                            </Tooltip>
                            <Button
                                shape="round"
                                onClick={() =>
                                    navigate(
                                        `/casino/awarded-promotion/${params.campaignId}/manage-games`,
                                    )
                                }
                                className="px-6 h-10 text-base font-medium mr-3"
                            >
                                Manage Games
                            </Button>
                        </>
                    )}
                    {campaignData && isUserCanManageCampaigns && (
                        <CampaignHeaderButton
                            id={params.campaignId}
                            status={campaignData.status}
                            data={campaignData}
                            requestCampaign={requestCampaign}
                        />
                    )}
                </div>
            </div>

            {/* Grand Total Summary */}
            {campaignSummary && !error && (
                <div className="px-6 mb-4">
                    <PromotionalTotalComponent
                        currentCurrencyCode={currencyCode}
                        data={{ ...campaignSummary, ...campaignData }}
                        onCurrencyChange={handleUpdateCurrency}
                    />
                </div>
            )}

            <div className="flex px-6 h-[calc(100vh-310px)]">
                {/* Left: Details Card */}
                <div className="flex flex-row w-1/3 h-full">
                    <div className="flex flex-col bg-white border border-gray-200 rounded-l-lg p-4 w-full overflow-y-auto">
                        {/* General Information Section */}
                        <div className="mb-4">
                            <div className="font-semibold text-base mb-2">General Information</div>
                            <dl className="divide-y divide-gray-200">
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Campaign Name</dt>
                                    <dd className="font-medium text-gray-900 text-sm">{campaignData?.name || '-'}</dd>
                                </div>
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Campaign ID</dt>
                                    <dd className="font-medium text-gray-900 text-sm">{params.campaignId || '-'}</dd>
                                </div>
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Campaign Reference</dt>
                                    <dd className="font-medium text-gray-900 text-sm">{campaignData?.externalCampaignId || '--'}</dd>
                                </div>
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Status</dt>
                                    <dd>
                                        <span
                                            className={`px-2 py-0.5 rounded text-xs font-semibold
                                            ${
                                                campaignData?.status === 'ONGOING'
                                                    ? 'bg-green-100 text-green-800'
                                                    : campaignData?.status === 'UPCOMING'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : campaignData?.status === 'ENDED'
                                                            ? 'bg-yellow-100 text-yellow-800'
                                                            : campaignData?.status === 'DISABLED'
                                                                ? 'bg-red-100 text-red-800'
                                                                : 'bg-gray-100 text-gray-800'
                                            }`}
                                        >
                                            {campaignData?.status || '-'}
                                        </span>
                                    </dd>
                                </div>
                                {canViewThirdPartyFeatures ? (
                                    <div className="flex flex-row justify-between items-center py-1.5">
                                        <dt className="text-gray-500 text-sm">Owner</dt>
                                        <dd className="font-medium text-gray-900 text-sm">
                                            {getCampaignOwnerDisplayName(campaignData)}
                                        </dd>
                                    </div>
                                ) : null}
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Rules URL</dt>
                                    <dd>
                                        {campaignData?.rulesUrl ? (
                                            <a
                                                href={campaignData.rulesUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-red-500 underline text-sm"
                                            >
                                                {campaignData.rulesUrl.length > 40
                                                    ? campaignData.rulesUrl.slice(0, 37) + '...'
                                                    : campaignData.rulesUrl}
                                            </a>
                                        ) : (
                                            <span className="text-sm">-</span>
                                        )}
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        {/* Player Stats Section */}
                        <div className="mb-4">
                            <div className="font-semibold text-base mb-2">Player Stats</div>
                            <dl className="divide-y divide-gray-200">
                                {/*<div className="flex flex-row justify-between items-center py-1.5">*/}
                                {/*    <dt className="text-gray-500 text-sm">Eligible Players</dt>*/}
                                {/*    <dd className="font-medium text-gray-900 text-sm">{campaignData?.eligiblePlayersNumber || '0'}</dd>*/}
                                {/*</div>*/}
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Playing</dt>
                                    <dd className="font-medium text-gray-900 text-sm">{campaignData?.playersWithPlayedRoundsNumber || '0'}</dd>
                                </div>
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Opted Out</dt>
                                    <dd className="font-medium text-gray-900 text-sm">{campaignData?.optOutPlayersNumber || '0'}</dd>
                                </div>
                            </dl>
                        </div>

                        {/* Schedule Section */}
                        <div className="mb-4">
                            <div className="font-semibold text-base mb-2">Schedule</div>
                            <dl className="divide-y divide-gray-200">
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Timezone</dt>
                                    <dd className="font-medium text-gray-900 text-sm">{campaignData?.timezone || '-'}</dd>
                                </div>
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Start at</dt>
                                    <dd className="font-medium text-gray-900 text-sm">
                                        {campaignData?.startDate
                                            ? dayjs(campaignData.startDate).format('HH:mm:ss DD/MM/YY')
                                            : '-'}
                                    </dd>
                                </div>
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">End at</dt>
                                    <dd className="font-medium text-gray-900 text-sm">
                                        {campaignData?.endDate
                                            ? dayjs(campaignData.endDate).format('HH:mm:ss DD/MM/YY')
                                            : '-'}
                                    </dd>
                                </div>
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Valid for (days)</dt>
                                    <dd className="font-medium text-gray-900 text-sm">
                                        {campaignData?.validForDays ? campaignData?.validForDays
                                            : '-'}
                                    </dd>
                                </div>
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Starts on</dt>
                                    <dd className="font-medium text-gray-900 text-sm">{campaignData?.startEvent ? campaignData?.startEvent === "ACCEPT" ? "Player Accepts Campaign" : "Player Added To Campaign" : "-"}</dd>
                                </div>
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Allow to use after the end date</dt>
                                    <dd className="font-medium text-gray-900 text-sm">{campaignData?.allowPostExpirationCompletion !== null ? campaignData?.allowPostExpirationCompletion ? 'Yes' : 'No' : '-'}</dd>
                                </div>
                            </dl>
                        </div>

                        {/* Cost Section */}
                        <div className="mb-4">
                            <div className="font-semibold text-base mb-2">Cost</div>
                            <dl className="divide-y divide-gray-200">
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Bet</dt>
                                    <dd className="font-medium text-gray-900 text-sm">
                                        {campaignData?.bet
                                            ? `${campaignData.bet / currencyUtils.getCurrencyDataByCode(campaignData.currencyCode).subunit} ${campaignData.currencyCode}`
                                            : '-'}
                                    </dd>
                                </div>
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Number of features per player</dt>
                                    <dd className="font-medium text-gray-900 text-sm">{campaignData?.betNumber || '-'}</dd>
                                </div>
                                <div className="flex flex-row justify-between items-center py-1.5">
                                    <dt className="text-gray-500 text-sm">Estimated Total Cost</dt>
                                    <dd className="font-medium text-gray-900 text-sm">
                                        {campaignData?.totalGivenRoundsCost
                                            ? `${campaignData.totalGivenRoundsCost / currencyUtils.getCurrencyDataByCode(campaignData.currencyCode).subunit} ${campaignData.currencyCode}`
                                            : '-'}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>
                {/* Right: Tabs for Players and Games */}
                <div className="flex w-2/3 bg-white rounded-r-lg border border-gray-200 border-l-0">
                    <Tabs
                        defaultActiveKey="players"
                        className="px-6 py-4 w-full h-full"
                        items={[
                            {
                                key: 'players',
                                label: <span className="font-medium">Players</span>,
                                children: params.campaignId ? (
                                    <PlayersTable
                                        campaignId={params.campaignId}
                                        currencyCode={currencyCode}
                                    />
                                ) : null,
                            },
                            {
                                key: 'games',
                                label: <span className="font-medium">Games</span>,
                                children: params.campaignId ? (
                                    <GamesTable campaignId={params.campaignId} />
                                ) : null,
                            },
                        ]}
                    />
                </div>
            </div>
        </div>
    );
};

export default AwardedCampaignControl;
